import { getStockTypeLabel } from "@/features/stocks/stock-type-labels";

const EXCHANGE_ORDER = {
  xbul: 0,
  xetr: 1,
  xfra: 2,
};

function normalize(value) {
  return value?.toString().trim().toLowerCase() || "";
}

function getStockTypeId(stock) {
  return parseInt(stock?.stid ?? stock?.tID ?? 0);
}

function getExchangeRank(stock) {
  const exchange = normalize(stock?.xc);
  return EXCHANGE_ORDER[exchange] ?? Number.MAX_SAFE_INTEGER;
}

export function getSearchCriteria(searchText) {
  const trimmedSearch = searchText?.toString().trim() || "";

  if (trimmedSearch.startsWith("//")) {
    return {
      mode: "name",
      query: trimmedSearch.substring(2).trim(),
    };
  }

  if (trimmedSearch.startsWith("/")) {
    return {
      mode: "code",
      query: trimmedSearch.substring(1).trim(),
    };
  }

  return {
    mode: "all",
    query: trimmedSearch,
  };
}

function getMatchScore(stock, searchCriteria) {
  const query = normalize(searchCriteria?.query);
  const mode = searchCriteria?.mode || "all";
  const code = normalize(stock?.code);
  const name = normalize(stock?.nEN);

  if (!query || query.length < 3) {
    return {
      rank: Number.MAX_SAFE_INTEGER,
      deviation: Number.MAX_SAFE_INTEGER,
      source: "none",
    };
  }

  if ("name" !== mode && code === query) {
    return { rank: 0, deviation: 0, source: "code" };
  }

  if ("code" !== mode && name === query) {
    return { rank: 1, deviation: 0, source: "name" };
  }

  if ("name" !== mode && code.includes(query)) {
    return {
      rank: 2,
      deviation: code.length - query.length,
      source: "code",
    };
  }

  if ("code" !== mode && name.startsWith(query)) {
    return {
      rank: "name" === mode ? 2 : 3,
      deviation: name.length - query.length,
      source: "name",
    };
  }

  if ("code" !== mode && name.includes(query)) {
    return {
      rank: "name" === mode ? 3 : 4,
      deviation: name.length - query.length,
      source: "name",
    };
  }

  return {
    rank: Number.MAX_SAFE_INTEGER,
    deviation: Number.MAX_SAFE_INTEGER,
    source: "none",
  };
}

function compareText(left, right) {
  return normalize(left).localeCompare(normalize(right));
}

function compareNameSearchResults(left, right, leftScore, rightScore) {
  const exchangeDifference = getExchangeRank(left) - getExchangeRank(right);
  if (0 !== exchangeDifference) {
    return exchangeDifference;
  }

  const deviationDifference = leftScore.deviation - rightScore.deviation;
  if (0 !== deviationDifference) {
    return deviationDifference;
  }

  const nameDifference = compareText(left?.nEN, right?.nEN);
  if (0 !== nameDifference) {
    return nameDifference;
  }

  return compareText(left?.code, right?.code);
}

export function sortSearchResults(stocks, searchCriteria) {
  if (!stocks || !Array.isArray(stocks)) {
    return [];
  }

  return [...stocks].sort((left, right) => {
    const leftScore = getMatchScore(left, searchCriteria);
    const rightScore = getMatchScore(right, searchCriteria);
    const rankDifference = leftScore.rank - rightScore.rank;
    if (0 !== rankDifference) {
      return rankDifference;
    }

    if ("name" === searchCriteria?.mode || ("name" === leftScore.source && "name" === rightScore.source)) {
      return compareNameSearchResults(left, right, leftScore, rightScore);
    }

    const deviationDifference = leftScore.deviation - rightScore.deviation;
    if (0 !== deviationDifference) {
      return deviationDifference;
    }

    const codeDifference = compareText(left?.code, right?.code);
    if (0 !== codeDifference) {
      return codeDifference;
    }

    const exchangeDifference = getExchangeRank(left) - getExchangeRank(right);
    if (0 !== exchangeDifference) {
      return exchangeDifference;
    }

    return compareText(left?.nEN, right?.nEN);
  });
}

export function addSearchResultTypeLabels(stocks, labels) {
  return stocks.map(stock => {
    const typeId = getStockTypeId(stock);
    const categoryKey = getSearchGroupKey(stock);
    return {
      ...stock,
      typeLabel: getStockTypeLabel(typeId, labels),
      categoryLabel: labels[categoryKey],
    };
  });
}

function getSearchGroupKey(stock) {
  const typeId = getStockTypeId(stock);
  if (1 === typeId || 4 === typeId) {
    return "equitiesAndEtfs";
  }
  if (5 === typeId) {
    return "reits";
  }
  if (2 === typeId || 3 === typeId) {
    return "bonds";
  }
  return "other";
}

export function groupSearchResults(stocks, labels) {
  const groups = [
    { key: "equitiesAndEtfs", label: labels.equitiesAndEtfs, stocks: [] },
    { key: "reits", label: labels.reits, stocks: [] },
    { key: "bonds", label: labels.bonds, stocks: [] },
    { key: "other", label: labels.other, stocks: [] },
  ];
  const groupsByKey = new Map(groups.map(group => [group.key, group]));

  addSearchResultTypeLabels(stocks, labels).forEach(stock => {
    groupsByKey.get(getSearchGroupKey(stock)).stocks.push(stock);
  });

  return groups.filter(group => 0 < group.stocks.length);
}
